import type { DemoDoc, Step } from '@mado/schema';
import {
  captionText,
  isStoryViewport,
  STORY_MAX_WIDTH_PX,
  storyDoc,
  storySource,
} from '@mado/schema/camera';
import { commonColor } from '@mado/schema/color';
import type { StoryPlan } from './cards';
import { el } from './stage';

/**
 * A landscape recording on a portrait phone: the phone walkthrough in the 9:16 video's layout
 * (cards.ts `planCards`; docs/plans/phone-walkthrough.md for the camera). The step's headline
 * (`Caption`) above a window of the screen's shape, its sentences as cards under it; the camera
 * holds one zoom for the demo and pans between targets inside the window (runtime.ts).
 *
 * Kept apart from runtime.ts so the story is one file to read, and so the
 * runtime only has to ask "is there a story here, and where is its window".
 */

/** What the headline says for a step — shared with the 9:16 export (`@mado/schema/camera`). */
export { captionText };

/** A story is told for this document on this viewport (`storyDoc`: shots, or a landscape capture without any). */
export function storyOn(doc: DemoDoc, viewport: { w: number; h: number }): boolean {
  return (
    storyDoc(doc) && viewport.w < STORY_MAX_WIDTH_PX && isStoryViewport(viewport, storySource(doc))
  );
}

export class Caption {
  readonly root: HTMLElement;
  private readonly text: HTMLElement;
  private readonly measure: HTMLElement;
  private shown = '';

  constructor() {
    this.root = el('div', 'mado-caption');
    this.root.setAttribute('aria-hidden', 'true');
    this.text = el('p', 'mado-caption-t');
    this.root.appendChild(this.text);
    // A hidden twin with the same box, to measure a caption before it shows.
    this.measure = el('div', 'mado-caption mado-caption--measure');
    this.measure.appendChild(el('p', 'mado-caption-t'));
  }

  /** Mount inside the viewport (the measuring twin goes with it). */
  mount(parent: HTMLElement, before: Node | null): void {
    parent.insertBefore(this.root, before);
    parent.insertBefore(this.measure, before);
  }

  /**
   * Height the headline needs for this step's text at this width, in CSS px —
   * 0 for a step with nothing to caption (no tooltip, no real name), so
   * `planCards` reserves no headline and the picture takes the room instead of
   * an empty bar with nothing in it (CLAUDE.md: a text-less target renders
   * cleanly, no empty bubble — the headline is the phone-story one).
   */
  heightFor(step: Step, width: number): number {
    const t = captionText(step);
    if (!t) return 0;
    this.measure.style.width = `${width}px`;
    this.measure.firstChild!.textContent = t;
    return this.measure.offsetHeight || 0;
  }

  /** Show the headline for a step laid out by `plan` — hidden outright when the
   *  step has nothing to caption, matching the 0 height `heightFor` already
   *  gave `plan`. */
  show(step: Step, plan: StoryPlan): void {
    const t = captionText(step);
    if (!t) {
      this.hide();
      return;
    }
    this.root.style.display = '';
    this.root.style.height = `${plan.caption.h}px`;
    this.root.style.top = `${plan.caption.y}px`;
    if (t !== this.shown) {
      this.shown = t;
      this.text.classList.remove('mado-caption-t--in');
      this.text.textContent = t;
      void this.text.offsetWidth;
      this.text.classList.add('mado-caption-t--in');
    }
  }

  hide(): void {
    this.root.style.display = 'none';
    this.shown = '';
  }
}

/* ── the gaps round a whole-screen view ─────────────────────────────────── */

const edges = new Map<string, Promise<string | null>>();

/**
 * A fill for the window behind a picture that does not cover it (the whole
 * screen of a landscape recording in a squarer window): the picture's own top
 * edge colour above its middle and its bottom edge colour below (the colour most
 * of each edge has, `commonColor`), read off the
 * document's inline placeholder, so the gaps look like more of the page, not
 * like black bars. Null when it cannot be read (no canvas, no placeholder).
 */
export function edgeFill(placeholder: string | undefined): Promise<string | null> {
  if (!placeholder || typeof document === 'undefined') return Promise.resolve(null);
  let hit = edges.get(placeholder);
  if (!hit) {
    hit = new Promise<string | null>((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const w = Math.max(1, Math.min(img.naturalWidth, 32));
          const h = Math.max(2, Math.min(img.naturalHeight, 32));
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          const g = c.getContext('2d', { willReadFrequently: true });
          if (!g) return resolve(null);
          g.drawImage(img, 0, 0, w, h);
          const row = (y: number): string => {
            const [r, gr, b] = commonColor(g.getImageData(0, y, w, 1).data, 4);
            return `rgb(${r},${gr},${b})`;
          };
          // A hard split, not a blend: two flat colours.
          resolve(`linear-gradient(${row(0)} 50%, ${row(h - 1)} 50%)`);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = placeholder;
    });
    edges.set(placeholder, hit);
  }
  return hit;
}
